/**
 * SFR Analytics Schemas
 *
 * Zod validation schemas for SFR Analytics API integration.
 * Issue #244: SFR Analytics API for CRM Enrichment, Batch Actions, and Outbound Prospecting.
 *
 * SFR Analytics provides:
 * - Buyer/borrower profile data
 * - Property ownership and history
 * - Market analytics and trends
 * - Lender activity insights
 */

import { z } from 'zod';

// ============================================================================
// COMMON SCHEMAS
// ============================================================================

/**
 * Address schema used across SFR Analytics responses
 */
export const SfrAddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().max(2).optional(),
  zipCode: z.string().max(10).optional(),
  county: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
export type SfrAddress = z.infer<typeof SfrAddressSchema>;

/**
 * Phone number schema
 */
export const SfrPhoneSchema = z.object({
  number: z.string(),
  type: z.enum(['mobile', 'home', 'work', 'unknown']).optional(),
  isPrimary: z.boolean().optional(),
});
export type SfrPhone = z.infer<typeof SfrPhoneSchema>;

/**
 * Email schema
 */
export const SfrEmailSchema = z.object({
  address: z.string().email(),
  isPrimary: z.boolean().optional(),
  isVerified: z.boolean().optional(),
});
export type SfrEmail = z.infer<typeof SfrEmailSchema>;

// ============================================================================
// BUYER/BORROWER PROFILE SCHEMAS
// ============================================================================

/**
 * Buyer search criteria
 * Supports both structured queries (firstName, address) and SFR Analytics API params (state, city, zips)
 */
export const BuyerSearchCriteriaSchema = z.object({
  // Structured search fields
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: SfrAddressSchema.optional(),
  propertyTypes: z.array(z.string()).optional(),
  minPurchasePrice: z.number().positive().optional(),
  maxPurchasePrice: z.number().positive().optional(),
  purchaseDateStart: z.string().optional(),
  purchaseDateEnd: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).default(0),
  // SFR Analytics API-specific fields (for direct API calls)
  state: z.string().max(2).optional(),
  states: z.union([z.string(), z.array(z.string())]).optional(),
  city: z.string().optional(),
  cities: z.union([z.string(), z.array(z.string())]).optional(),
  zips: z.union([z.string(), z.array(z.string())]).optional(),
  buyer_type: z.string().optional(),
  buyer_properties_count_min: z.number().int().nonnegative().optional(),
  buyer_properties_count_max: z.number().int().nonnegative().optional(),
  sale_amt_min: z.number().nonnegative().optional(),
  sale_amt_max: z.number().nonnegative().optional(),
  recording_date_start: z.string().optional(),
  recording_date_end: z.string().optional(),
  page: z.number().int().positive().optional(),
  page_size: z.number().int().positive().optional(),
});
export type BuyerSearchCriteria = z.infer<typeof BuyerSearchCriteriaSchema>;

/**
 * Buyer profile schema
 */
export const BuyerProfileSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  fullName: z.string().optional(),
  emails: z.array(SfrEmailSchema).optional(),
  phones: z.array(SfrPhoneSchema).optional(),
  addresses: z.array(SfrAddressSchema).optional(),
  currentAddress: SfrAddressSchema.optional(),
  // Investment profile
  investorType: z.enum(['individual', 'llc', 'trust', 'corporation', 'partnership', 'unknown']).optional(),
  totalPropertiesOwned: z.number().int().nonnegative().optional(),
  totalPortfolioValue: z.number().nonnegative().optional(),
  averagePurchasePrice: z.number().nonnegative().optional(),
  // Activity
  lastPurchaseDate: z.string().datetime().optional(),
  lastSaleDate: z.string().datetime().optional(),
  // Scores
  investorScore: z.number().min(0).max(100).optional(),
  creditIndicator: z.enum(['excellent', 'good', 'fair', 'poor', 'unknown']).optional(),
  // Metadata
  dataQualityScore: z.number().min(0).max(100).optional(),
  lastUpdated: z.string().datetime().optional(),
});
export type BuyerProfile = z.infer<typeof BuyerProfileSchema>;

/**
 * Buyer search response
 */
export const BuyerSearchResponseSchema = z.object({
  buyers: z.array(BuyerProfileSchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  searchId: z.string().optional(),
});
export type BuyerSearchResponse = z.infer<typeof BuyerSearchResponseSchema>;

// ============================================================================
// PROPERTY SCHEMAS
// ============================================================================

/**
 * Property search criteria
 * Supports both structured queries and SFR Analytics API params (radius, months_back)
 */
export const PropertySearchCriteriaSchema = z.object({
  // Structured search fields
  address: z.union([SfrAddressSchema, z.string()]).optional(),
  apn: z.string().optional(), // Assessor's Parcel Number
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  propertyType: z
    .enum(['single_family', 'multi_family', 'condo', 'townhouse', 'land', 'commercial', 'other'])
    .optional(),
  minValue: z.number().positive().optional(),
  maxValue: z.number().positive().optional(),
  minBedrooms: z.number().int().nonnegative().optional(),
  maxBedrooms: z.number().int().nonnegative().optional(),
  minBathrooms: z.number().nonnegative().optional(),
  maxBathrooms: z.number().nonnegative().optional(),
  minSquareFeet: z.number().positive().optional(),
  maxSquareFeet: z.number().positive().optional(),
  yearBuiltStart: z.number().int().min(1800).max(2100).optional(),
  yearBuiltEnd: z.number().int().min(1800).max(2100).optional(),
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).default(0),
  // SFR Analytics API-specific fields (for nearby sales / comparable searches)
  radius: z.number().positive().optional(), // Search radius in miles
  months_back: z.number().int().positive().optional(), // Months of history to search
  sale_price_min: z.number().nonnegative().optional(),
  sale_price_max: z.number().nonnegative().optional(),
  property_types: z.union([z.string(), z.array(z.string())]).optional(),
});
export type PropertySearchCriteria = z.infer<typeof PropertySearchCriteriaSchema>;

/**
 * Property ownership record
 */
export const PropertyOwnershipSchema = z.object({
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  ownerType: z
    .enum(['individual', 'llc', 'trust', 'corporation', 'partnership', 'bank', 'government', 'unknown'])
    .optional(),
  ownershipPercentage: z.number().min(0).max(100).optional(),
  acquisitionDate: z.string().datetime().optional(),
  acquisitionPrice: z.number().nonnegative().optional(),
  isCurrentOwner: z.boolean().optional(),
});
export type PropertyOwnership = z.infer<typeof PropertyOwnershipSchema>;

/**
 * Property transaction record
 */
export const PropertyTransactionSchema = z.object({
  transactionId: z.string(),
  transactionType: z.enum(['sale', 'refinance', 'transfer', 'foreclosure', 'other']),
  transactionDate: z.string().datetime(),
  amount: z.number().nonnegative().optional(),
  buyer: z.string().optional(),
  seller: z.string().optional(),
  lender: z.string().optional(),
  documentNumber: z.string().optional(),
});
export type PropertyTransaction = z.infer<typeof PropertyTransactionSchema>;

/**
 * Property profile schema
 */
export const PropertyProfileSchema = z.object({
  id: z.string(),
  apn: z.string().optional(),
  address: SfrAddressSchema,
  // Property details
  propertyType: z.string().optional(),
  bedrooms: z.number().int().nonnegative().optional(),
  bathrooms: z.number().nonnegative().optional(),
  squareFeet: z.number().positive().optional(),
  lotSize: z.number().positive().optional(),
  yearBuilt: z.number().int().optional(),
  // Valuation
  estimatedValue: z.number().nonnegative().optional(),
  lastSalePrice: z.number().nonnegative().optional(),
  lastSaleDate: z.string().datetime().optional(),
  assessedValue: z.number().nonnegative().optional(),
  taxAmount: z.number().nonnegative().optional(),
  // Ownership
  currentOwner: PropertyOwnershipSchema.optional(),
  ownershipHistory: z.array(PropertyOwnershipSchema).optional(),
  // Transactions
  transactionHistory: z.array(PropertyTransactionSchema).optional(),
  // Loan info
  currentLoanAmount: z.number().nonnegative().optional(),
  currentLender: z.string().optional(),
  loanOriginationDate: z.string().datetime().optional(),
  // Metadata
  dataQualityScore: z.number().min(0).max(100).optional(),
  lastUpdated: z.string().datetime().optional(),
});
export type PropertyProfile = z.infer<typeof PropertyProfileSchema>;

/**
 * Property search response
 */
export const PropertySearchResponseSchema = z.object({
  properties: z.array(PropertyProfileSchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  searchId: z.string().optional(),
});
export type PropertySearchResponse = z.infer<typeof PropertySearchResponseSchema>;

// ============================================================================
// MARKET ANALYTICS SCHEMAS
// ============================================================================

/**
 * Market analytics request
 */
export const MarketAnalyticsRequestSchema = z.object({
  geography: z.object({
    state: z.string().max(2).optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().max(10).optional(),
    msa: z.string().optional(), // Metropolitan Statistical Area
  }),
  propertyType: z.enum(['single_family', 'multi_family', 'condo', 'all']).optional(),
  dateRange: z
    .object({
      start: z.string().datetime(),
      end: z.string().datetime(),
    })
    .optional(),
  metrics: z
    .array(
      z.enum([
        'median_sale_price',
        'average_sale_price',
        'sales_volume',
        'days_on_market',
        'price_per_sqft',
        'inventory_level',
        'foreclosure_rate',
        'appreciation_rate',
      ]),
    )
    .optional(),
});
export type MarketAnalyticsRequest = z.infer<typeof MarketAnalyticsRequestSchema>;

/**
 * Market metric data point
 */
export const MarketMetricSchema = z.object({
  metric: z.string(),
  value: z.number(),
  period: z.string(), // e.g., "2024-Q1", "2024-01"
  change: z.number().optional(), // Percentage change from prior period
  trend: z.enum(['up', 'down', 'stable']).optional(),
});
export type MarketMetric = z.infer<typeof MarketMetricSchema>;

/**
 * Market analytics response
 */
export const MarketAnalyticsResponseSchema = z.object({
  geography: z.object({
    state: z.string().optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
    msa: z.string().optional(),
  }),
  metrics: z.array(MarketMetricSchema),
  summary: z
    .object({
      medianSalePrice: z.number().optional(),
      averageSalePrice: z.number().optional(),
      totalSalesVolume: z.number().int().optional(),
      averageDaysOnMarket: z.number().optional(),
      pricePerSquareFoot: z.number().optional(),
    })
    .optional(),
  generatedAt: z.string().datetime(),
});
export type MarketAnalyticsResponse = z.infer<typeof MarketAnalyticsResponseSchema>;

// ============================================================================
// LENDER ACTIVITY SCHEMAS
// ============================================================================

/**
 * Lender activity request
 */
export const LenderActivityRequestSchema = z.object({
  geography: z.object({
    state: z.string().max(2).optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().max(10).optional(),
  }),
  lenderName: z.string().optional(),
  loanType: z.enum(['purchase', 'refinance', 'heloc', 'all']).optional(),
  dateRange: z
    .object({
      start: z.string().datetime(),
      end: z.string().datetime(),
    })
    .optional(),
  limit: z.number().int().min(1).max(100).default(25),
});
export type LenderActivityRequest = z.infer<typeof LenderActivityRequestSchema>;

/**
 * Lender profile schema
 */
export const LenderProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['bank', 'credit_union', 'mortgage_company', 'private_lender', 'other']).optional(),
  // Activity metrics
  totalLoansOriginated: z.number().int().nonnegative().optional(),
  totalLoanVolume: z.number().nonnegative().optional(),
  averageLoanAmount: z.number().nonnegative().optional(),
  marketShare: z.number().min(0).max(100).optional(),
  // Loan type breakdown
  purchaseLoans: z.number().int().nonnegative().optional(),
  refinanceLoans: z.number().int().nonnegative().optional(),
  // Contact info
  contactInfo: z
    .object({
      phone: z.string().optional(),
      email: z.string().email().optional(),
      website: z.string().url().optional(),
    })
    .optional(),
});
export type LenderProfile = z.infer<typeof LenderProfileSchema>;

/**
 * Lender activity response
 */
export const LenderActivityResponseSchema = z.object({
  lenders: z.array(LenderProfileSchema),
  totalCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  geography: z.object({
    state: z.string().optional(),
    county: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
  }),
  generatedAt: z.string().datetime(),
});
export type LenderActivityResponse = z.infer<typeof LenderActivityResponseSchema>;

// ============================================================================
// BATCH OPERATION SCHEMAS
// ============================================================================

/**
 * Batch enrichment request
 */
export const BatchEnrichmentRequestSchema = z.object({
  targetType: z.enum(['buyer', 'property', 'lead', 'consumer']),
  targetIds: z.array(z.string()).min(1).max(100),
  enrichmentTypes: z.array(
    z.enum([
      'buyer_profile',
      'property_history',
      'ownership_history',
      'transaction_history',
      'market_analytics',
      'contact_info',
    ]),
  ),
  options: z
    .object({
      skipExisting: z.boolean().default(false),
      forceRefresh: z.boolean().default(false),
    })
    .optional(),
});
export type BatchEnrichmentRequest = z.infer<typeof BatchEnrichmentRequestSchema>;

/**
 * Batch enrichment result
 */
export const BatchEnrichmentResultSchema = z.object({
  targetId: z.string(),
  status: z.enum(['success', 'failed', 'skipped']),
  enrichedData: z.record(z.string(), z.unknown()).optional(),
  error: z.string().optional(),
});
export type BatchEnrichmentResult = z.infer<typeof BatchEnrichmentResultSchema>;

/**
 * Batch enrichment response
 */
export const BatchEnrichmentResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(['pending', 'in_progress', 'completed', 'failed', 'partial']),
  totalCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  results: z.array(BatchEnrichmentResultSchema).optional(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
});
export type BatchEnrichmentResponse = z.infer<typeof BatchEnrichmentResponseSchema>;

// ============================================================================
// PROSPECT IMPORT SCHEMAS
// ============================================================================

/**
 * Prospect import criteria
 */
export const ProspectImportCriteriaSchema = z.object({
  // Buyer criteria
  investorType: z.array(z.string()).optional(),
  minPropertiesOwned: z.number().int().nonnegative().optional(),
  maxPropertiesOwned: z.number().int().nonnegative().optional(),
  minPortfolioValue: z.number().nonnegative().optional(),
  maxPortfolioValue: z.number().nonnegative().optional(),
  // Geographic criteria
  geography: z
    .object({
      states: z.array(z.string().max(2)).optional(),
      counties: z.array(z.string()).optional(),
      cities: z.array(z.string()).optional(),
      zipCodes: z.array(z.string().max(10)).optional(),
    })
    .optional(),
  // Activity criteria
  lastPurchaseDateStart: z.string().datetime().optional(),
  lastPurchaseDateEnd: z.string().datetime().optional(),
  // Scores
  minInvestorScore: z.number().min(0).max(100).optional(),
  // Import options
  limit: z.number().int().min(1).max(500).default(100),
  assignTo: z.string().optional(), // User ID to assign prospects to
  campaignId: z.string().optional(),
  tags: z.array(z.string()).optional(),
});
export type ProspectImportCriteria = z.infer<typeof ProspectImportCriteriaSchema>;

/**
 * Prospect import response
 */
export const ProspectImportResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(['pending', 'searching', 'importing', 'completed', 'failed', 'cancelled']),
  totalFound: z.number().int().nonnegative(),
  imported: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  errors: z.array(z.string()).optional(),
});
export type ProspectImportResponse = z.infer<typeof ProspectImportResponseSchema>;

// ============================================================================
// SFR ANALYTICS CONNECTOR CONFIG
// ============================================================================

/**
 * SFR Analytics connector configuration schema
 */
export const SfrAnalyticsConfigSchema = z.object({
  apiKey: z.string().min(1),
  baseUrl: z.string().url().optional(),
  environment: z.enum(['sandbox', 'production']).default('production'),
  rateLimitPerMinute: z.number().int().positive().default(60),
  timeout: z.number().int().positive().default(30000),
});
export type SfrAnalyticsConfig = z.infer<typeof SfrAnalyticsConfigSchema>;
